/**
 * Who is signed in. The server's cookie is the identity; this only mirrors
 * it, and reconciles against /api/auth/me on every load.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiGet, apiPost } from '../api/client';

export interface Account {
  email: string;
  name: string;
  admin: boolean;
}

interface Ctx {
  account: Account | null | undefined; // undefined = not yet asked
  authMode: 'disabled' | 'code' | 'unconfigured' | 'unknown';
  signIn: (email: string, name: string, code: string) => Promise<string | null>;
  signOut: () => void;
}

const SessionCtx = createContext<Ctx | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const [account, setAccount] = useState<Account | null | undefined>(undefined);
  const [authMode, setAuthMode] = useState<Ctx['authMode']>('unknown');

  useEffect(() => {
    void apiGet<Account | null>('/api/auth/me').then((r) => setAccount(r.status === 'ready' ? r.data : null));
    void apiGet<{ mode: Ctx['authMode'] }>('/api/auth/config').then((r) => {
      if (r.status === 'ready' && r.data) setAuthMode(r.data.mode);
    });
  }, []);

  const signIn = useCallback(async (email: string, name: string, code: string): Promise<string | null> => {
    const r = await apiPost<Account>('/api/auth/login', { email, name, code });
    if (r.status === 'ready') {
      setAccount(r.data);
      return null;
    }
    return r.status === 'error' || r.status === 'not_configured' ? r.message : 'Could not sign in.';
  }, []);

  const signOut = useCallback(() => {
    void apiPost('/api/auth/signout', {});
    setAccount(null);
  }, []);

  const value = useMemo(() => ({ account, authMode, signIn, signOut }), [account, authMode, signIn, signOut]);
  return <SessionCtx.Provider value={value}>{children}</SessionCtx.Provider>;
}

export function useSession(): Ctx {
  const ctx = useContext(SessionCtx);
  if (!ctx) throw new Error('useSession outside SessionProvider');
  return ctx;
}
